/**
 * Сроки и обеспечения договора (ДГ-20, ДГ-10, ФН-07): обязательства с ответственным и сроком, сроки уведомлений
 * (отсчёт от события), банковские гарантии и гарантийный срок; график на год вперёд по всем договорам.
 * Данные читаются с адресов договоров; внешним участникам и людям без права блок не показывается.
 */
import {
  type ContractObligations,
  NOTICE_EVENTS,
  type NoticeEvent,
  OBLIGATION_KINDS,
  type ObligationKind,
  type ObligationRow,
  type ObligationSchedule,
  SECURITY_KINDS,
  type SecurityKind,
  type SecurityRow,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date, tenge, todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const hidden = (err: unknown) => err instanceof ApiError && (err.status === 403 || err.status === 404);

function dueText(daysLeft: number | null, overdue: boolean) {
  if (daysLeft == null) return null;
  if (overdue) return `просрочено на ${Math.abs(daysLeft)} дн.`;
  if (daysLeft === 0) return 'сегодня';
  return `через ${daysLeft} дн.`;
}

function ObligationLine({
  o,
  canEdit,
  onChanged,
  showContract,
}: {
  o: ObligationRow;
  canEdit: boolean;
  onChanged: () => void;
  showContract?: boolean;
}) {
  const act = useMutation({
    mutationFn: (path: 'done' | 'cancel' | 'start') =>
      api(`/contracts/obligations/${o.id}/${path}`, { method: 'POST', body: {} }),
    onSuccess: () => {
      onChanged();
      toast('Сохранено');
    },
  });
  const left = o.status === 'open' ? dueText(o.daysLeft, o.overdue) : null;
  return (
    <li className="flex flex-col gap-2 py-3 text-[14px] sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="text-ink">{o.title}</div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-2">
          <span>{OBLIGATION_KINDS[o.kind as ObligationKind] ?? o.kind}</span>
          {showContract ? (
            <Link
              to="/contracts/$contractId"
              params={{ contractId: o.contractId }}
              className="text-accent underline"
            >
              {o.contractNumber}
            </Link>
          ) : null}
          {showContract ? <span>{o.projectCode}</span> : null}
          {o.status === 'waiting' ? (
            <span>
              ждёт события «{NOTICE_EVENTS[o.triggerEvent as NoticeEvent] ?? o.triggerEvent}», затем{' '}
              {o.noticeDays} дн.
            </span>
          ) : o.dueOn ? (
            <span>до {date(o.dueOn)}</span>
          ) : null}
          {o.responsibleName ? <span>отвечает {o.responsibleName}</span> : null}
        </div>
        {o.note ? <p className="mt-1 text-[12px] text-ink-2">{o.note}</p> : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {left ? <Badge tone={o.overdue ? 'danger' : 'info'}>{left}</Badge> : null}
        {o.status === 'done' ? (
          <Badge tone="success">выполнено {o.doneOn ? date(o.doneOn) : ''}</Badge>
        ) : null}
        {o.status === 'cancelled' ? <Badge tone="neutral">отменено</Badge> : null}
        {canEdit && o.status === 'waiting' ? (
          <Button size="sm" variant="secondary" loading={act.isPending} onClick={() => act.mutate('start')}>
            Событие случилось
          </Button>
        ) : null}
        {canEdit && (o.status === 'open' || o.status === 'waiting') ? (
          <>
            {o.status === 'open' ? (
              <Button
                size="sm"
                variant="secondary"
                loading={act.isPending}
                onClick={() => act.mutate('done')}
              >
                Выполнено
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" loading={act.isPending} onClick={() => act.mutate('cancel')}>
              Отменить
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

function SecurityLine({
  s,
  canEdit,
  onChanged,
  showContract,
}: {
  s: SecurityRow;
  canEdit: boolean;
  onChanged: () => void;
  showContract?: boolean;
}) {
  const [extending, setExtending] = useState(false);
  const [until, setUntil] = useState('');
  const ret = useMutation({
    mutationFn: () => api(`/contracts/securities/${s.id}/return`, { method: 'POST', body: {} }),
    onSuccess: () => {
      onChanged();
      toast('Отмечено: возвращено');
    },
  });
  const ext = useMutation({
    mutationFn: () =>
      api(`/contracts/securities/${s.id}/extend`, { method: 'POST', body: { validUntil: until } }),
    onSuccess: () => {
      setExtending(false);
      onChanged();
      toast('Срок продлён');
    },
  });
  return (
    <li className="flex flex-col gap-2 py-3 text-[14px] sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="text-ink">
          {SECURITY_KINDS[s.kind as SecurityKind] ?? s.kind}
          {s.number ? ` № ${s.number}` : ''}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-2">
          {s.bank ? <span>{s.bank}</span> : null}
          {showContract ? (
            <Link
              to="/contracts/$contractId"
              params={{ contractId: s.contractId }}
              className="text-accent underline"
            >
              {s.contractNumber}
            </Link>
          ) : null}
          <span>
            {s.issuedOn ? `${date(s.issuedOn)} — ` : 'до '}
            {date(s.validUntil)}
          </span>
          {s.amount != null ? <span className="num">{tenge(s.amount)}</span> : null}
          {s.commission != null ? <span className="num">комиссия {tenge(s.commission)}</span> : null}
          {s.responsibleName ? <span>отвечает {s.responsibleName}</span> : null}
        </div>
        {s.note ? <p className="mt-1 text-[12px] text-ink-2">{s.note}</p> : null}
        {extending ? (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <Field label="Новый срок">
              <Input
                type="date"
                value={until}
                min={s.validUntil}
                onChange={(e) => setUntil(e.target.value)}
              />
            </Field>
            <Button
              size="sm"
              variant="primary"
              loading={ext.isPending}
              disabled={!until}
              onClick={() => ext.mutate()}
            >
              Продлить
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setExtending(false)}>
              Отмена
            </Button>
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {s.status === 'returned' ? (
          <Badge tone="neutral">возвращено {s.returnedOn ? date(s.returnedOn) : ''}</Badge>
        ) : s.overdue ? (
          <Badge tone="danger">срок прошёл</Badge>
        ) : s.toReturn ? (
          <Badge tone="warning">договор закрыт — вернуть</Badge>
        ) : s.daysLeft <= s.remindDays ? (
          <Badge tone="warning">{dueText(s.daysLeft, false)}</Badge>
        ) : null}
        {canEdit && s.status === 'active' ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => setExtending(true)}>
              Продлить
            </Button>
            <Button size="sm" variant="secondary" loading={ret.isPending} onClick={() => ret.mutate()}>
              Возвращена
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

function AddObligationDialog({
  contractId,
  people,
  onClose,
  onSaved,
}: {
  contractId: string;
  people: Array<{ id: string; fullName: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({
    kind: 'payment' as ObligationKind,
    title: '',
    dueOn: todayLocal(),
    responsibleId: '',
    note: '',
    event: 'claim' as NoticeEvent,
    noticeDays: '28',
    repeatMonths: '',
    repeatCount: '12',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const notice = f.kind === 'notice';
  const save = useMutation({
    mutationFn: () =>
      api(`/contracts/${contractId}/obligations`, {
        method: 'POST',
        body: {
          kind: f.kind,
          title: f.title.trim(),
          dueOn: notice ? undefined : f.dueOn,
          responsibleId: f.responsibleId || undefined,
          note: f.note.trim() || undefined,
          ...(notice ? { triggerEvent: f.event, noticeDays: Number(f.noticeDays) } : {}),
          ...(!notice && f.repeatMonths
            ? { repeatMonths: Number(f.repeatMonths), repeatCount: Number(f.repeatCount) }
            : {}),
        },
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      toast('Обязательство добавлено');
    },
  });
  const ready = f.title.trim().length >= 2 && (notice ? Number(f.noticeDays) > 0 : Boolean(f.dueOn));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новое обязательство"
      description="Срок, ответственный и напоминание. Срок уведомления отсчитывается от события."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            Добавить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Вид">
          <Select value={f.kind} onChange={(e) => set('kind', e.target.value as ObligationKind)}>
            {(Object.keys(OBLIGATION_KINDS) as ObligationKind[]).map((k) => (
              <option key={k} value={k}>
                {OBLIGATION_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Что нужно сделать">
          <Input value={f.title} onChange={(e) => set('title', e.target.value)} />
        </Field>
        {notice ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="После какого события">
              <Select value={f.event} onChange={(e) => set('event', e.target.value as NoticeEvent)}>
                {(Object.keys(NOTICE_EVENTS) as NoticeEvent[]).map((k) => (
                  <option key={k} value={k}>
                    {NOTICE_EVENTS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Дней на уведомление" hint="Например, 28 по FIDIC 2017, п. 20.2.1">
              <Input
                type="number"
                min={1}
                value={f.noticeDays}
                onChange={(e) => set('noticeDays', e.target.value)}
              />
            </Field>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Срок">
              <Input type="date" value={f.dueOn} onChange={(e) => set('dueOn', e.target.value)} />
            </Field>
            <Field label="Повторять каждые, мес.">
              <Input
                type="number"
                min={1}
                max={12}
                placeholder="не повторять"
                value={f.repeatMonths}
                onChange={(e) => set('repeatMonths', e.target.value)}
              />
            </Field>
            <Field label="Сколько раз">
              <Input
                type="number"
                min={2}
                max={36}
                disabled={!f.repeatMonths}
                value={f.repeatCount}
                onChange={(e) => set('repeatCount', e.target.value)}
              />
            </Field>
          </div>
        )}
        <Field
          label="Ответственный"
          hint="Без ответственного напоминание получают те, кто ведёт договоры проекта"
        >
          <Select value={f.responsibleId} onChange={(e) => set('responsibleId', e.target.value)}>
            <option value="">Не назначен</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Примечание">
          <Textarea rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}

function AddSecurityDialog({
  contractId,
  people,
  moneyHidden,
  onClose,
  onSaved,
}: {
  contractId: string;
  people: Array<{ id: string; fullName: string }>;
  moneyHidden: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({
    kind: 'performance' as SecurityKind,
    bank: '',
    number: '',
    amount: '',
    commission: '',
    issuedOn: '',
    validUntil: '',
    remindDays: '30',
    responsibleId: '',
    note: '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const period = f.kind === 'warranty_period';
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  const save = useMutation({
    mutationFn: () =>
      api(`/contracts/${contractId}/securities`, {
        method: 'POST',
        body: {
          kind: f.kind,
          bank: f.bank.trim() || undefined,
          number: f.number.trim() || undefined,
          amount: moneyHidden || period ? undefined : num(f.amount),
          commission: moneyHidden || period ? undefined : num(f.commission),
          issuedOn: f.issuedOn || undefined,
          validUntil: f.validUntil,
          remindDays: Number(f.remindDays) || 30,
          responsibleId: f.responsibleId || undefined,
          note: f.note.trim() || undefined,
        },
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      toast('Обеспечение добавлено');
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новое обеспечение"
      description="Банковская гарантия, аккредитив или гарантийный срок по объекту. За выбранное число дней до окончания придёт напоминание."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!f.validUntil}
            onClick={() => save.mutate()}
          >
            Добавить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Вид">
          <Select value={f.kind} onChange={(e) => set('kind', e.target.value as SecurityKind)}>
            {(Object.keys(SECURITY_KINDS) as SecurityKind[]).map((k) => (
              <option key={k} value={k}>
                {SECURITY_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {period ? null : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Банк">
              <Input value={f.bank} onChange={(e) => set('bank', e.target.value)} />
            </Field>
            <Field label="Номер">
              <Input value={f.number} onChange={(e) => set('number', e.target.value)} />
            </Field>
            {moneyHidden ? null : (
              <>
                <Field label="Сумма, ₸">
                  <Input
                    type="number"
                    min={0}
                    value={f.amount}
                    onChange={(e) => set('amount', e.target.value)}
                  />
                </Field>
                <Field label="Комиссия, ₸">
                  <Input
                    type="number"
                    min={0}
                    value={f.commission}
                    onChange={(e) => set('commission', e.target.value)}
                  />
                </Field>
              </>
            )}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Выдана">
            <Input type="date" value={f.issuedOn} onChange={(e) => set('issuedOn', e.target.value)} />
          </Field>
          <Field label="Действует до">
            <Input type="date" value={f.validUntil} onChange={(e) => set('validUntil', e.target.value)} />
          </Field>
          <Field label="Напомнить за, дн.">
            <Input
              type="number"
              min={1}
              max={365}
              value={f.remindDays}
              onChange={(e) => set('remindDays', e.target.value)}
            />
          </Field>
        </div>
        <Field label="Ответственный">
          <Select value={f.responsibleId} onChange={(e) => set('responsibleId', e.target.value)}>
            <option value="">Не назначен</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Примечание">
          <Textarea rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}

/** Блок карточки договора: обязательства, сроки уведомлений, обеспечения */
export function ContractObligationsPanel({ contractId, canEdit }: { contractId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ctr', 'obligations', contractId],
    queryFn: () => api<ContractObligations>(`/contracts/${contractId}/obligations`),
    retry: false,
  });
  const [adding, setAdding] = useState<'ob' | 'sec' | null>(null);
  if (q.isPending || q.isError) return q.isError && !hidden(q.error) ? <ErrorBox error={q.error} /> : null;
  const d = q.data;
  const changed = () => {
    void qc.invalidateQueries({ queryKey: ['ctr', 'obligations'] });
    void qc.invalidateQueries({ queryKey: ['inbox'] });
  };
  const open = d.obligations.filter((o) => o.status === 'open' || o.status === 'waiting');
  const closed = d.obligations.filter((o) => o.status === 'done' || o.status === 'cancelled');
  return (
    <>
      <Panel
        title="Сроки и обязательства"
        meta="что и когда нужно сделать по договору"
        actions={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => setAdding('ob')}
            >
              Добавить
            </Button>
          ) : null
        }
      >
        {open.length || closed.length ? (
          <ul className="divide-y divide-line">
            {[...open, ...closed].map((o) => (
              <ObligationLine key={o.id} o={o} canEdit={canEdit} onChanged={changed} />
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">
            Обязательств нет. Добавьте платежи и поставки с повтором на год или срок уведомления (например, 28
            дней на извещение о претензии).
          </p>
        )}
      </Panel>
      <Panel
        title="Обеспечения и гарантии"
        meta="банковские гарантии, аккредитивы, гарантийный срок"
        actions={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => setAdding('sec')}
            >
              Добавить
            </Button>
          ) : null
        }
      >
        {d.securities.length ? (
          <ul className="divide-y divide-line">
            {d.securities.map((s) => (
              <SecurityLine key={s.id} s={s} canEdit={canEdit} onChanged={changed} />
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">Обеспечений нет.</p>
        )}
        {d.moneyHidden && d.securities.length ? (
          <p className="mt-2 text-[12px] text-ink-2">
            Суммы гарантий видны сотрудникам с доступом к суммам договоров.
          </p>
        ) : null}
      </Panel>
      {adding === 'ob' ? (
        <AddObligationDialog
          contractId={contractId}
          people={d.people}
          onClose={() => setAdding(null)}
          onSaved={changed}
        />
      ) : null}
      {adding === 'sec' ? (
        <AddSecurityDialog
          contractId={contractId}
          people={d.people}
          moneyHidden={d.moneyHidden}
          onClose={() => setAdding(null)}
          onSaved={changed}
        />
      ) : null}
    </>
  );
}

/** График на год вперёд по всем договорам — над списком договоров */
export function ObligationsSchedulePanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ctr', 'obligations', 'schedule'],
    queryFn: () => api<ObligationSchedule>('/contracts/obligations/schedule'),
    retry: false,
  });
  const [open, setOpen] = useState(false);
  if (q.isPending || q.isError) return null;
  const d = q.data;
  const total = d.obligations.length + d.securities.length;
  if (!total) return null;
  const changed = () => {
    void qc.invalidateQueries({ queryKey: ['ctr', 'obligations'] });
    void qc.invalidateQueries({ queryKey: ['inbox'] });
  };
  const max = Math.max(1, ...d.months.map((m) => m.obligations + m.securities));
  return (
    <Panel
      title="Сроки и гарантии на год вперёд"
      meta={`просрочено ${d.overdue}, ближайшие 30 дней — ${d.next30}`}
      actions={
        <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? 'Свернуть' : 'Показать все'}
        </Button>
      }
    >
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-6 lg:grid-cols-12" aria-label="Сроки по месяцам">
        {d.months.map((m) => {
          const n = m.obligations + m.securities;
          return (
            <li key={m.month} className="flex flex-col gap-1 text-[12px] text-ink-2">
              <span>
                {m.month.slice(5)}.{m.month.slice(2, 4)}
              </span>
              <span className="h-2 rounded bg-surface-2">
                <span className="block h-2 rounded bg-accent" style={{ width: `${(n / max) * 100}%` }} />
              </span>
              <span className="num text-ink">{n}</span>
            </li>
          );
        })}
      </ul>
      {open ? (
        <ul className="mt-3 divide-y divide-line">
          {d.obligations.map((o) => (
            <ObligationLine key={o.id} o={o} canEdit={false} onChanged={changed} showContract />
          ))}
          {d.securities.map((s) => (
            <SecurityLine key={s.id} s={s} canEdit={false} onChanged={changed} showContract />
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}
